import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { KuiTreeSelectNode } from 'app/key-ui/tree-select/tree-select.component';
import { FilterChangeEvent } from 'app/pages/status/overview/overview.component';
import { AssetFilterService } from 'app/services';
import { FilterType } from 'app/services/assets/asset-filter.service';
import { BaseComponent } from 'app/shared/components/base/base.component';
import { findNested } from 'app/shared/utils/find-nested';
import * as arrayToTree from 'array-to-tree';
import { Observable } from 'rxjs';
import { AppService } from 'app/app.service';

@Component({
    selector: 'key-asset-filter',
    templateUrl: 'asset-filter.component.html',
})
export class KeyAssetFilterComponent extends BaseComponent implements OnInit {
    _filter: FilterChangeEvent[];
    @Input() filter: Observable<FilterChangeEvent[]>;
    @Output() filterChanged: EventEmitter<FilterChangeEvent[]> = new EventEmitter();

    filters: KuiTreeSelectNode[];
    selectedItem: KuiTreeSelectNode;
    selection: {
        [property: string]: KuiTreeSelectNode;
    };
    treeStates = {
        costcentre: null,
        assetgroup: null,
        assetcategory: null,
    };

    constructor(
        public filterService: AssetFilterService,
        public i18n: TranslateService,
        public changeDetector: ChangeDetectorRef,
        public app: AppService
    ) {
        super();
    }

    async ngOnInit() {
        this.on(this.filter, filter => {
            this._filter = filter;
            (this.filters || []).forEach(f => {
                f.children = this.applySelection(f.children, f.id, this._filter);
            });
        });

        this.on(this.filterService.filters$, async () => {
            const treeOptions = {
                parentId: 'root',
                parentProperty: 'parent',
            };

            const originalCostCentres = this.filterService.costCentres ? this.filterService.costCentres.map(a => ({ ...a })) : [];
            const originalAssetCategories = this.filterService.assetCategories ? this.filterService.assetCategories.map(a => ({ ...a })) : [];
            const originalAssetGroups = this.filterService.assetGroups ? this.filterService.assetGroups.map(a => ({ ...a })) : [];
            const originalAssetTypes = this.filterService.assetTypes ? this.filterService.assetTypes.map(a => ({ ...a })) : [];

            const user = await this.app.api.accounts.getUser(this.app.user.id).catch(() => ({} as any)); // if the user fails we can still show tree items, so just sending an empty object

            // if user has a cost centre assigned then only show the cost centre and its children
            const costCentres = user.costCentre ? this.getItemInTree(user.costCentre.id, arrayToTree(originalCostCentres, treeOptions)) : arrayToTree(originalCostCentres, treeOptions);
            const assetCategories = arrayToTree(originalAssetCategories, treeOptions);
            const assetGroups = arrayToTree(originalAssetGroups, treeOptions);
            const assetTypes = arrayToTree(originalAssetTypes, treeOptions);

            const removeParents = (list) => {
                for (const item of list) {
                    if (item.parent) {
                        delete item.parent;
                    }

                    if (item.children) {
                        item.children = removeParents(item.children);
                    }

                }

                return list;
            };

            this.filters = [
                {
                    name: this.i18n.instant('STATUS.LIST.ACTION.COST_CENTRE'),
                    children: costCentres ? removeParents(this.applySelection(costCentres, 'costcentre', this._filter)) : [],
                    id: 'costcentre',
                },
                {
                    name: this.i18n.instant('STATUS.LIST.ACTION.ASSET_CATEGORY'),
                    children: assetCategories ? removeParents(this.applySelection(this.sortArray(assetCategories), 'assetcategory', this._filter)) : [],
                    id: 'assetcategory',
                },
                {
                    name: this.i18n.instant('STATUS.LIST.ACTION.ASSET_GROUP'),
                    children: assetGroups ? removeParents(this.applySelection(this.sortArray(assetGroups), 'assetgroup', this._filter)) : [],
                    id: 'assetgroup',
                },
                {
                    name: this.i18n.instant('STATUS.LIST.ACTION.ASSET_TYPE'),
                    children: assetTypes ? removeParents(this.applySelection(this.sortArray(assetTypes), 'assettype', this._filter)) : [],
                    id: 'assettype',
                },
            ];

            setTimeout(() => this.changeDetector.markForCheck(), 0);
        });
    }

    sortArray(arr) {
        const sortedArr = arr.map(item => {
            if (item.children) {
                item.children.sort((a, b) => a.name.localeCompare(b.name));
                this.sortArray(item.children);
            }
            return item;
        }).sort((a, b) => a.name.localeCompare(b.name));
        return sortedArr;
    }

    getItemInTree(id: string, tree: KuiTreeSelectNode[]): KuiTreeSelectNode[] {
        let item = null;

        const getItem = treeItem => {
            if (treeItem.id === id) {
                item = treeItem;
                return;
            }

            if (!!(treeItem.children && treeItem.children.length)) {
                treeItem.children.forEach(getItem);
            }
        };

        tree.forEach(getItem);

        return [item];
    }

    applySelection(input, kind, filter) {
        if (!filter.length) {
            return input;
        }

        const filterIds = filter.map(f => f.id);

        for (const node of input) {
            node.kind = kind;

            if (filterIds.indexOf(node.id) > -1) {
                node.selected = true;
            }

            if (node.children) {
                node.children = this.applySelection(node.children, kind, filter);
            }
        }

        return input;
    }

    filtersChanged(selectedItem: KuiTreeSelectNode, filter: KuiTreeSelectNode) {
        const filterType = selectedItem.id as FilterType;

        // navigate back when filter is changed
        setTimeout(() => this.changeSelection(null));

        // There's no change event between deactivate and activate events, so the filter does change
        // solution is to always make sure the current filter type is removed
        const dedupedFilter = this._filter.filter(f => f.type !== filterType);

        if (filter.eventName === 'deactivate') {
            this.filterChanged.emit(dedupedFilter);
            this.filterService.setFilter(filterType, null);

            return;
        }

        const selection = filter && filter.node && filter.node.data ? filter.node.data.id : null;

        this.filterChanged.emit(dedupedFilter.concat([{ type: filterType, id: selection }]));
        this.filterService.setFilter(filterType, selection);
    }

    changeSelection(item: KuiTreeSelectNode, event?: Event) {
        // tree states needs reference of this in current runloop
        setTimeout(() => {
            this.selectedItem = item;
            this.changeDetector.markForCheck();
        });

        if (event) {
            event.stopImmediatePropagation();
        }
    }

    getSelection(selectedItem: KuiTreeSelectNode): string {
        if (!selectedItem) {
            return null;
        }

        const match = this._filter.find(filter => filter.type === selectedItem.id);

        if (!match) {
            return null;
        }

        const nestedMatch = findNested(match.id, this.filters);

        return nestedMatch ? nestedMatch.name : null;
    }

    getNonEmptyFilters(filters: KuiTreeSelectNode[]): KuiTreeSelectNode[] {
        const flatten = (input) => {
            let nodes = [];

            for (const item of input) {
                nodes = nodes.concat([item]);

                if (item.children) {
                    nodes = nodes.concat(flatten(item.children));
                }
            }

            return nodes;
        };

        return (filters || []).filter(item => {
            if (item.id !== 'assettype') {
                return flatten(item.children).length > 1;
            }

            return true;
        });
    }
}
